import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Types for the trip results data
export interface Trip {
    id: string;
    ferryId: string;
    routeId: string;
    date: string;
    time: string;
    recurrenceType: 'SINGLE' | 'WEEKLY';
    status: boolean;
    availableTickets: number;
}

export interface PassengerResult {
    type: 'ADULT' | 'SENIOR' | 'CHILD';
    quantity: number;
    price: number;
}

export interface VehicleResult {
    type: 'VEHICLE' | 'FOOT_PASSENGER';
    quantity: number;
    price: number;
    details: {
        height: string;
        length: string;
        type: string; // Add the specific vehicle type (motorcycle, car, etc.)
        vehicle_return: boolean;
    };
}

export interface TripResultsData {
    departureTrips: Trip[];
    returnTrips?: Trip[]; // Optional for one-way trips
    passengers: PassengerResult[];
    vehicle: VehicleResult | null; // Can be null for foot passengers
    totalFare: number;
}

export interface TripResultsState {
    // Search results data
    results: TripResultsData | null;

    // Selected trips for booking
    selectedDepartureTrip: Trip | null;
    selectedReturnTrip: Trip | null;

    // UI state
    isLoading: boolean;
    error: string | null;

    // Booking flow state
    hasSearchResults: boolean;
}

const initialState: TripResultsState = {
    results: null,
    selectedDepartureTrip: null,
    selectedReturnTrip: null,
    isLoading: false,
    error: null,
    hasSearchResults: false
};

const tripResultsSlice = createSlice({
    name: 'tripResults',
    initialState,
    reducers: {
        // Set search results
        setTripResults: (state, action: PayloadAction<TripResultsData>) => {
            state.results = action.payload;
            state.hasSearchResults = true;
            state.error = null;

            // Auto-select first departure trip if available
            if (action.payload.departureTrips.length > 0) {
                state.selectedDepartureTrip = action.payload.departureTrips[0];
            }

            // Auto-select first return trip if available (for round trips)
            if (action.payload.returnTrips && action.payload.returnTrips.length > 0) {
                state.selectedReturnTrip = action.payload.returnTrips[0];
            } else {
                // Clear return trip selection for one-way trips
                state.selectedReturnTrip = null;
            }
        },

        // Select specific trips
        setSelectedDepartureTrip: (state, action: PayloadAction<Trip | null>) => {
            state.selectedDepartureTrip = action.payload;
        },

        setSelectedReturnTrip: (state, action: PayloadAction<Trip | null>) => {
            state.selectedReturnTrip = action.payload;
        },

        // Loading and error states
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

        setError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.isLoading = false;
        },

        // Clear results
        clearTripResults: (state) => {
            state.results = null;
            state.selectedDepartureTrip = null;
            state.selectedReturnTrip = null;
            state.hasSearchResults = false;
            state.error = null;
            state.isLoading = false;
        },

        // Calculate total fare based on selected trips
        updateTotalFare: (state) => {
            if (!state.results) return;

            // This is a placeholder - you might want to implement
            // dynamic fare calculation based on selected trips
            // For now, we'll keep the original total fare
        }
    }
});

export const {
    setTripResults,
    setSelectedDepartureTrip,
    setSelectedReturnTrip,
    setLoading,
    setError,
    clearTripResults,
    updateTotalFare
} = tripResultsSlice.actions;

export default tripResultsSlice.reducer;